import { useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { NJButton, NJFormItem, NJHeading, NJText } from '@engie-group/fluid-design-system-react';
import { calculatedConsumption, effectiveConsumption, fmt } from './domain';
import { DecisionNote } from './DecisionPanel';
import type { Building, Energy, Project } from './types';

export function BuildingPanel({ building: b, project, save, dirty, onDecision }: {
  building: Building; project: Project; save: (id: string, e: Energy, value: number | undefined) => void;
  dirty: (value: boolean) => void; onDecision: (id: string) => void;
}) {
  const form = useForm<{ heat: string; cold: string }>({
    defaultValues: { heat: b.corrections.heat?.toString() ?? '', cold: b.corrections.cold?.toString() ?? '' },
  });
  useEffect(() => { dirty(form.formState.isDirty); }, [dirty, form.formState.isDirty]);
  const submit = form.handleSubmit(values => {
    for (const e of ['heat', 'cold'] as const) {
      const v = values[e] === '' ? undefined : Number(values[e]);
      if (v !== b.corrections[e]) save(b.id, e, v);
    }
    form.reset(values);
  });
  const revert = (e: Energy) => {
    save(b.id, e, undefined);
    form.resetField(e, { defaultValue: '' });
  };
  return <section className="sp-panel sp-stack">
    <NJText as="div" className="pu-label">Données communes · bâtiment {b.id}</NJText>
    <NJHeading as="h2" scale="md">{b.name}</NJHeading>
    <NJText scale="sm">{b.usage} · {b.period} · {fmt(b.surface)} m² chauffés · source v{b.sourceVersion}</NJText>
    {b.conflict && <div role="alert" className="sp-warning">
      <NJText>Supprimé dans UrbanMap. Copie et abonnements conservés ; résolution à arbitrer.</NJText>
      <DecisionNote id="conflict" onOpen={onDecision} />
    </div>}
    <form onSubmit={submit} className="sp-stack">
      {(['heat', 'cold'] as const).map(e => <div key={e} className="pu-card sp-stack">
        <NJHeading as="h3" scale="sm">{e === 'heat' ? 'Chaleur' : 'Froid'}</NJHeading>
        <NJText as="div">Calculée : <strong>{fmt(calculatedConsumption(b, project, e))} MWh/an</strong></NJText>
        <NJText as="div">Effective enregistrée : <strong>{fmt(effectiveConsumption(b, project, e))} MWh/an</strong>
          {b.corrections[e] !== undefined ? ' · correction locale' : ' · calcul projet'}</NJText>
        <Controller name={e} control={form.control} rules={{ validate: value =>
          value === '' || (Number.isFinite(Number(value)) && Number(value) > 0) ||
          'Démonstration : saisir une valeur positive ; zéro et bornes restent à arbitrer.',
        }} render={({ field, fieldState }) => <NJFormItem id={`correction-${e}`} label={`Correction finale ${e === 'heat' ? 'chaleur' : 'froid'} · MWh/an`}
          type="number" step="any" value={field.value} onBlur={field.onBlur}
          onChange={event => { if (event.target instanceof HTMLInputElement) field.onChange(event.target.value); }}
          hasError={!!fieldState.error} subscriptMessage={fieldState.error?.message ?? 'Vide = valeur calculée. Pas de seconde correction DJU.'} />} />
        <NJButton label={`Revenir à la valeur calculée · ${e === 'heat' ? 'chaleur' : 'froid'}`} scale="sm"
          emphasis="minimal" disabled={b.corrections[e] === undefined} onClick={() => revert(e)} />
      </div>)}
      {form.formState.isDirty && <NJText role="status">Modifications non enregistrées. Les plans utilisent encore les valeurs enregistrées.</NJText>}
      <div className="sp-row">
        <NJButton label="Enregistrer les corrections" type="submit" disabled={!form.formState.isDirty} />
        <NJButton label="Annuler les corrections" emphasis="subtle" disabled={!form.formState.isDirty} onClick={() => form.reset()} />
      </div>
    </form>
    <DecisionNote id="bounds" onOpen={onDecision} />
    <DecisionNote id="targeting" onOpen={onDecision} />
    <NJText scale="sm">Les plans calculés utilisant ce bâtiment sont signalés à recalculer, sans lancement.
      Démonstration conservatoire toutes énergies ; ciblage exact à arbitrer.</NJText>
  </section>;
}
